import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";

function badgeColor(status) {
  const map = {
    pending:    "#f59e0b",
    processing: "#3b82f6",
    completed:  "#22c55e",
    cancelled:  "#ef4444",
    new:        "#3b82f6",
    read:       "#6b7280",
    replied:    "#22c55e",
    paid:       "#22c55e",
    unpaid:     "#f59e0b",
  };
  return map[status] || "#9ca3af";
}

/* ─────────────────────────────────
   Sub-components
   ───────────────────────────────── */
function StatCard({ icon, label, value, trend, accent = "var(--admin-red)" }) {
  return (
    <div className="ad-stat-card" style={{ "--accent": accent }}>
      <div className="ad-stat-card__bar" />
      <div className="ad-stat-card__top">
        <span className="ad-stat-card__icon">{icon}</span>
        <span className="ad-stat-card__label">{label}</span>
      </div>
      <div className="ad-stat-card__value">{value ?? "—"}</div>
      {trend && (
        <div className="ad-stat-card__trend">
          <span className="ad-stat-card__trend-dot" />
          {trend}
        </div>
      )}
    </div>
  );
}

function Badge({ status }) {
  const color = badgeColor(status);
  return (
    <span className="ad-badge" style={{ "--bc": color }}>
      {status}
    </span>
  );
}

/* ─────────────────────────────────
   AdminDashboard
   ───────────────────────────────── */
export default function AdminDashboard() {
  const [stats,    setStats]    = useState(null);
  const [orders,   setOrders]   = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        // Fire requests in parallel; each is individually guarded so one
        // failure doesn't blank the whole dashboard.
        const [ordersData, messagesData] = await Promise.allSettled([
          apiFetch("/api/admin/orders?limit=5"),
          apiFetch("/api/admin/messages?limit=5"),
        ]);

        if (cancelled) return;

        const safeOrders   = ordersData.status   === "fulfilled" ? (ordersData.value?.orders   ?? ordersData.value   ?? []) : [];
        const safeMessages = messagesData.status === "fulfilled" ? (messagesData.value?.messages ?? messagesData.value ?? []) : [];

        setOrders(Array.isArray(safeOrders)   ? safeOrders   : []);
        setMessages(Array.isArray(safeMessages) ? safeMessages : []);

        // Build stats from what we have
        setStats({
          orders:   Array.isArray(safeOrders)   ? safeOrders.length   : 0,
          messages: Array.isArray(safeMessages) ? safeMessages.length : 0,
        });
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminLayout pageTag="ADMIN" pageTitle="Dashboard">

      {error && (
        <div style={{
          background: "rgba(214,40,40,.12)", border: "1px solid rgba(214,40,40,.35)",
          borderLeft: "3px solid #d62828", color: "#f07070",
          padding: "12px 16px", fontSize: ".85rem",
        }}>
          ⚠ Could not load some dashboard data: {error}
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="ad-stats">
        {loading ? (
          <>
            <div className="ad-stat-skeleton" />
            <div className="ad-stat-skeleton" />
            <div className="ad-stat-skeleton" />
          </>
        ) : (
          <>
            <StatCard icon="🗒"  label="Recent Orders"   value={stats?.orders}   trend="Last 5 orders"   accent="#3b82f6" />
            <StatCard icon="✉"   label="Recent Messages" value={stats?.messages} trend="Last 5 messages"  accent="#f59e0b" />
            <StatCard icon="⚙"   label="System"          value="OK"              trend="All services up"  accent="#22c55e" />
          </>
        )}
      </div>

      {/* ── Quick actions ── */}
      <div className="ad-section">
        <div className="ad-section__head">
          <h2 className="ad-section__title">Quick Actions</h2>
        </div>
        <div className="ad-quick-grid">
          {[
            { icon: "☰",  label: "Shop Products",   path: "/admin/shop/products" },
            { icon: "🗒",  label: "Orders",          path: "/admin/orders"        },
            { icon: "✉",   label: "Messages",        path: "/admin/messages"      },
            { icon: "◈",   label: "Quotations",      path: "/admin/quotations"    },
            { icon: "♟",   label: "Customers",       path: "/admin/customers"     },
            { icon: "◫",   label: "Slideshow",       path: "/admin/slideshow"     },
          ].map(({ icon, label, path }) => (
            <button
              key={path}
              className="ad-quick-card"
              onClick={() => window.location.href = path}
            >
              <span className="ad-quick-card__icon">{icon}</span>
              <span className="ad-quick-card__label">{label}</span>
              <span className="ad-quick-card__arrow">→</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Recent Orders ── */}
      <div className="ad-section">
        <div className="ad-section__head">
          <h2 className="ad-section__title">Recent Orders</h2>
          <button className="ad-section__link" onClick={() => window.location.href = "/admin/orders"}>
            View all →
          </button>
        </div>

        {loading ? (
          <div className="ad-table-skeleton" />
        ) : orders.length === 0 ? (
          <div className="ad-empty">No orders found.</div>
        ) : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td><span className="ad-table__link">{o.order_number}</span></td>
                    <td>{o.customer_name}</td>
                    <td>R {Number(o.total ?? 0).toFixed(2)}</td>
                    <td><Badge status={o.status} /></td>
                    <td><Badge status={o.payment_status} /></td>
                    <td style={{ color: "var(--admin-muted)", fontSize: ".8rem" }}>
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("en-ZA") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Recent Messages ── */}
      <div className="ad-section">
        <div className="ad-section__head">
          <h2 className="ad-section__title">Recent Messages</h2>
          <button className="ad-section__link" onClick={() => window.location.href = "/admin/messages"}>
            View all →
          </button>
        </div>

        {loading ? (
          <div className="ad-table-skeleton" />
        ) : messages.length === 0 ? (
          <div className="ad-empty">No messages found.</div>
        ) : (
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td style={{ color: "var(--admin-muted)", fontSize: ".82rem" }}>{m.email}</td>
                    <td>{m.subject || "—"}</td>
                    <td><Badge status={m.status} /></td>
                    <td style={{ color: "var(--admin-muted)", fontSize: ".8rem" }}>
                      {m.created_at ? new Date(m.created_at).toLocaleDateString("en-ZA") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </AdminLayout>
  );
}